import { Box, Divider, Flex, Text } from '@byecode/ui'
import type { FlowNode, RichTextEditorProps, StationMessageActionConfig } from '@lighthouse/shared'
import {
    DEVICE_ID_OPTION,
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    PAGE_LINK_OPTION,
    RichTextEditor,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import { GroupRequired } from '../Common/GroupRequired'
import { MessageTargetPerson } from './component/MessageTargetPerson'

interface StationMessageActionConfigureProps {
    allParentNodes: FlowNode[]
    nodeType?: string
    value: StationMessageActionConfig
    onChange: (value: StationMessageActionConfig) => void
}

const SCxRightFill = styled.div`
    width: 100%;
    margin-top: 8px;

    & div[data-node-view-wrapper] {
        top: 0 !important;
    }
`

export const StationMessageActionConfigure: React.FunctionComponent<StationMessageActionConfigureProps> = ({
    allParentNodes,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { type } = useFlowContainerContext()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()

    const upstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes),
                allUpstreamNodes: allParentNodes,
                dataSourceList
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const userOption = useGetUserRecordOptions()

    const isNotAutoMation = type !== 'automation'

    const richTextConfig: RichTextEditorProps['config'] = useMemo(() => {
        return {
            variable: {
                pageOptions: isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined,
                upstreamOptions,
                userOption
            },
            mode: 'text-style'
        }
    }, [isNotAutoMation, upstreamOptions, userOption])

    return (
        <>
            <GroupRequired required label="配置参数">
                <Flex direction="column" gap={16}>
                    <Box px={8}>
                        <Box py={6}>
                            <Text size={14}>标题</Text>
                        </Box>
                        <SCxRightFill>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                placeholder="请输入文字或添加变量"
                                value={value.title}
                                onBlur={title => onChange({ ...value, title })}
                                autoHeight
                                upstreamOptions={upstreamOptions}
                                userOption={userOption}
                                pageOptions={isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                                dataSourceList={dataSourceList}
                            />
                        </SCxRightFill>
                    </Box>
                    <Box px={8}>
                        <Box py={6}>
                            <Text size={14}>正文</Text>
                        </Box>
                        <SCxRightFill>
                            <RichTextEditor
                                disabled={disabledWithVersion}
                                config={richTextConfig}
                                value={value.content}
                                styles={{
                                    editorContainer: {
                                        width: '100%',
                                        minHeight: 200,
                                        borderRadius: 6,
                                        border: '1px solid var(--color-gray-200)'
                                    }
                                }}
                                disableToolbar={false}
                                onChange={content => onChange({ ...value, content })}
                            />
                        </SCxRightFill>
                    </Box>
                </Flex>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired required label="收信人">
                <Box px={8}>
                    <MessageTargetPerson
                        workflowList={workflowList}
                        dataSourceList={dataSourceList}
                        parentNodes={allParentNodes}
                        value={value.addresseeIds}
                        onChange={addresseeIds => onChange({ ...value, addresseeIds })}
                    />
                </Box>
            </GroupRequired>
        </>
    )
}
